/**
 * The "Needs your attention" block on the manager Team page: which direct
 * reports have something wrong, and what.
 *
 * One entry per PERSON rather than per problem — a manager acts on a person, so
 * someone who is both under-booked and hasn't logged their time is one entry
 * carrying two flags, not two rows to work through.
 *
 * Pure. Every gate is a company-wide constant from `project-guidance.ts`; there
 * are deliberately no per-manager thresholds, so one definition of
 * "under-booked" holds across every screen in the app.
 */

import {
  currentMonthKey,
  LOW_LOGGED_PERCENT,
  OVER_COMMITTED_PERCENT,
  previousMonthKey,
  UNDER_GUIDED_PERCENT,
} from "@/lib/persons/project-guidance";
import {
  isUnderGuided,
  loggedInMonth,
  type TeamGuidanceMonth,
  type TeamMemberSummary,
} from "@/lib/persons/team-summary";
import type { PtoRequest } from "@/lib/pto";

/** A month guided below the company gate, from the current month on. */
export interface UnderBookedFlag {
  kind: "underBooked";
  /** The FIRST offending month, `YYYY-MM`. */
  month: string;
  guidedHours: number | null;
  capacityHours: number | null;
  allocationPercent: number | null;
}

/** A month guided beyond capacity, from the current month on. */
export interface OverCommittedFlag {
  kind: "overCommitted";
  /** The FIRST offending month, `YYYY-MM`. */
  month: string;
  guidedHours: number | null;
  capacityHours: number | null;
  allocationPercent: number | null;
}

/** Little or nothing logged for the last month that has finished. */
export interface NotLoggedFlag {
  kind: "notLogged";
  /** The last COMPLETE month, `YYYY-MM` — the current one is still being filled in. */
  month: string;
  loggedHours: number;
  capacityHours: number;
  /** True when nothing at all was logged, which reads differently from "some". */
  none: boolean;
}

/** A date range a report has requested but nobody has approved yet. */
export interface PendingLeaveRange {
  startDate: string;
  endDate: string;
  /** Working days in the range — an estimate, per {@link PtoRequest.days}. */
  days: number;
}

/**
 * Leave a report has requested that is still awaiting a decision.
 *
 * Approval happens in NOS, not Enable, so this is a nudge to go and act
 * elsewhere rather than something the manager can clear from this page.
 */
export interface LeavePendingFlag {
  kind: "leavePending";
  /** True when this manager is the named approver on at least one request. */
  yours: boolean;
  /** Soonest first, as the feed already orders them. */
  requests: PendingLeaveRange[];
}

export type TeamFlag = UnderBookedFlag | OverCommittedFlag | NotLoggedFlag | LeavePendingFlag;

/** One direct report who needs the manager's attention, and why. */
export interface TeamAttentionEntry {
  personId: string;
  name: string;
  title: string | null;
  avatarUrl: string | null;
  /** Never empty — a report with nothing wrong gets no entry at all. */
  flags: TeamFlag[];
}

/**
 * A month with real capacity is the precondition for EVERY booking claim here.
 *
 * `isUnderGuided` treats an unguided month as under-guided, which is right for
 * a person NOS knows — an unset month is the strongest version of the signal.
 * But a month with no capacity either means NOS has no record of that person at
 * all, and "nothing booked for September" is then a claim about someone we know
 * nothing about. The table below may still colour such a month; a sentence
 * naming a person is held to a higher bar than a shaded cell.
 */
function measurable(month: TeamGuidanceMonth): boolean {
  return month.capacityHours !== null && month.capacityHours > 0;
}

function underBooked(months: TeamGuidanceMonth[], current: string): UnderBookedFlag | null {
  // From the current month on. A past month is history: flagging it would light
  // up every row permanently and there is nothing a manager can do about it.
  const found = months.find((m) => m.month >= current && measurable(m) && isUnderGuided(m));
  if (!found) return null;
  return {
    kind: "underBooked",
    month: found.month,
    guidedHours: found.guidedHours,
    capacityHours: found.capacityHours,
    allocationPercent: found.allocationPercent,
  };
}

function overCommitted(months: TeamGuidanceMonth[], current: string): OverCommittedFlag | null {
  const found = months.find(
    (m) =>
      m.month >= current && measurable(m) && (m.allocationPercent ?? 0) > OVER_COMMITTED_PERCENT,
  );
  if (!found) return null;
  return {
    kind: "overCommitted",
    month: found.month,
    guidedHours: found.guidedHours,
    capacityHours: found.capacityHours,
    allocationPercent: found.allocationPercent,
  };
}

/**
 * Timesheets for the last finished month.
 *
 * Compared against LOGGED hours of every kind, leave included: a person off
 * sick for a month has logged their time correctly and must not be flagged for
 * it. A month whose capacity NOS doesn't know is skipped rather than flagged —
 * with no capacity there is nothing to be short of, and treating the gap as
 * zero would flag every person NOS has never heard of.
 */
function notLogged(member: TeamMemberSummary, current: string): NotLoggedFlag | null {
  const key = previousMonthKey(current);
  const found = member.months.find((m) => m.month === key);
  const capacityHours = found?.capacityHours ?? null;
  if (capacityHours === null || capacityHours <= 0) return null;

  const loggedHours = loggedInMonth(member, key);
  if (loggedHours >= (capacityHours * LOW_LOGGED_PERCENT) / 100) return null;
  return { kind: "notLogged", month: key, loggedHours, capacityHours, none: loggedHours === 0 };
}

/**
 * `null` pending leave means the read FAILED, not that there is none — so no
 * flag is raised and the page says it could not check. Reporting "nothing
 * awaiting approval" on the strength of a failed call is exactly the wrong
 * thing to tell a manager by accident.
 */
function leavePending(personId: string, pending: PtoRequest[], managerPersonId: string) {
  const mine = pending.filter((r) => r.personId === personId);
  if (mine.length === 0) return null;
  const flag: LeavePendingFlag = {
    kind: "leavePending",
    yours: mine.some((r) => r.approverPersonId === managerPersonId),
    requests: mine.map((r) => ({ startDate: r.startDate, endDate: r.endDate, days: r.days })),
  };
  return flag;
}

/**
 * How many reports have NO month NOS holds a capacity for, and so could not be
 * judged at all.
 *
 * The page needs this to keep its reassuring empty state honest: "everyone is
 * booked and up to date" said about people we have no figures for is the same
 * false all-clear an empty block would be.
 */
export function unassessedReports(members: TeamMemberSummary[]): number {
  return members.filter((m) => !m.months.some(measurable)).length;
}

export function buildTeamAttention(input: {
  members: TeamMemberSummary[];
  pendingLeave: PtoRequest[] | null;
  managerPersonId: string;
  now?: Date;
}): TeamAttentionEntry[] {
  const current = currentMonthKey(input.now ?? new Date());
  const entries: TeamAttentionEntry[] = [];

  for (const m of input.members) {
    const flags: TeamFlag[] = [];
    const under = underBooked(m.months, current);
    if (under) flags.push(under);
    const over = overCommitted(m.months, current);
    if (over) flags.push(over);
    const logged = notLogged(m, current);
    if (logged) flags.push(logged);
    const leave = input.pendingLeave
      ? leavePending(m.personId, input.pendingLeave, input.managerPersonId)
      : null;
    if (leave) flags.push(leave);

    if (flags.length > 0) {
      entries.push({
        personId: m.personId,
        name: m.name,
        title: m.title,
        avatarUrl: m.avatarUrl,
        flags,
      });
    }
  }

  // Worst first: the count of things wrong, then how badly under-booked they
  // are, then name — so the order is stable for two reports in the same state.
  return entries.sort(
    (a, b) =>
      b.flags.length - a.flags.length ||
      bookingShortfall(b) - bookingShortfall(a) ||
      a.name.localeCompare(b.name),
  );
}

/**
 * How far below the gate a report's first under-booked month sits, 0 when they
 * have no under-booked month at all. Ranking only — never shown.
 */
function bookingShortfall(entry: TeamAttentionEntry): number {
  const under = entry.flags.find((f): f is UnderBookedFlag => f.kind === "underBooked");
  if (!under) return 0;
  return UNDER_GUIDED_PERCENT - (under.allocationPercent ?? 0);
}
